import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, Pause, ExternalLink, AlertTriangle } from 'lucide-react';
import { useFreshAudioSrc } from '../../hooks/useFreshAudioSrc.js';

/**
 * InlineAudioPlayer — 仿 CallsPage 的內嵌錄音播放器
 *
 * 簡化版：播放 / 暫停 / 進度條 / 倍速 / 新分頁開啟
 * 沒有拖曳 seek（夠用就好，CallsPage 上有完整版要用再抽）
 */
export function InlineAudioPlayer({ src, refresh }) {
    const { t } = useTranslation('survey');
    const audioRef = useRef(null);
    // 錄音網址 10 分鐘過期（SRA R11）→ 出錯先重拿一次新網址，還是不行才顯示錯誤
    const fresh = useFreshAudioSrc(src, refresh);
    const retried = useRef(false);
    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [rate, setRate] = useState(1);
    const [error, setError] = useState(false);

    useEffect(() => {
        setProgress(0);
        setDuration(0);
        setError(false);
        retried.current = false;
    }, [src]);

    if (!src) return null;

    const toggle = () => {
        const a = audioRef.current;
        if (!a) return;
        if (a.paused) a.play(); else a.pause();
    };

    const cycleRate = () => {
        const rates = [1, 1.25, 1.5, 2];
        const idx = rates.indexOf(rate);
        const next = rates[(idx + 1) % rates.length];
        setRate(next);
        if (audioRef.current) audioRef.current.playbackRate = next;
    };

    if (error) {
        return (
            <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2.5 flex items-center gap-2 text-xs text-amber-700">
                <AlertTriangle size={14} />
                {/* 只顯示檔名，不印簽章參數 */}
                {t('audio.loadError', { src: String(fresh.src || '').split('?')[0].split('/').pop() })}
                <a href={fresh.src} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 hover:underline">
                    {t('audio.openNewTab')} <ExternalLink size={11} />
                </a>
            </div>
        );
    }

    const pct = duration ? (progress / duration) * 100 : 0;

    return (
        <div className="bg-white border border-cream-200 rounded-md px-3 py-2.5 flex items-center gap-3">
            <audio
                ref={audioRef}
                src={fresh.src}
                preload="metadata"
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onTimeUpdate={(e) => setProgress(e.target.currentTime || 0)}
                onLoadedMetadata={(e) => { setDuration(e.target.duration || 0); fresh.onLoadedMetadata(e); }}
                onError={(e) => {
                    if (refresh && !retried.current) { retried.current = true; fresh.onError(e); return; }
                    setError(true);
                }}
            />
            <button
                onClick={toggle}
                className="size-9 rounded-full bg-accent-500 hover:bg-accent-700 text-white flex items-center justify-center transition shrink-0"
            >
                {playing ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
            </button>

            <div className="flex-1 min-w-0">
                <div className="h-3 flex items-center cursor-pointer"
                    onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const ratio = (e.clientX - rect.left) / rect.width;
                        if (audioRef.current && duration) {
                            audioRef.current.currentTime = ratio * duration;
                        }
                    }}
                >
                    <div className="w-full h-1.5 bg-cream-200 rounded-full overflow-hidden">
                        <div className="h-full bg-accent-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                </div>
                <div className="flex justify-between mono-caption mt-1">
                    <span>{fmt(progress)}</span>
                    <span>{fmt(duration)}</span>
                </div>
            </div>

            <button
                onClick={cycleRate}
                className="font-mono text-xs px-2 py-1 bg-cream-100 hover:bg-cream-200 rounded text-ink-700"
            >
                {rate}×
            </button>

            <a href={src} target="_blank" rel="noreferrer" className="text-ink-400 hover:text-ink-700">
                <ExternalLink size={13} />
            </a>
        </div>
    );
}

function fmt(sec) {
    if (!sec || !isFinite(sec)) return '0:00';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
}
